import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronLeft, Copy, ExternalLink, MoreHorizontal, Package, Pencil, RefreshCw, Search, Trash2 } from "lucide-react";
import { ApiError } from "../lib/api";
import { PackInfo, copyText, packApi, packLink } from "../lib/packApi";
import { openPackLink } from "../lib/telegram";
import { ScreenHeader } from "./ScreenHeader";
import { BannerCarousel } from "./BannerCarousel";
import { BottomSheet } from "./BottomSheet";
import { PackLottie } from "./PackLottie";
import { PackDetail } from "./PackDetail";
import { DeletePackSheet, RenamePackSheet } from "./PackSheets";

function PackRow({ pack, onOpen, onMenu }: { pack: PackInfo; onOpen: () => void; onMenu: () => void }) {
  return (
    <div className="glass-card flex items-center gap-1 pe-1">
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 flex items-center gap-3.5 p-3 text-right">
        <div className="w-14 h-14 rounded-2xl glass-pill shrink-0 flex items-center justify-center">
          {pack.cover_sticker_id ? (
            <PackLottie id={pack.cover_sticker_id} size={44} fallback={<Package size={20} className="text-tg-hint" />} />
          ) : (
            <Package className="text-tg-hint" size={22} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="block font-semibold truncate">{pack.title}</span>
          <span className="block text-xs text-tg-hint mt-0.5">{pack.sticker_count} ملصق</span>
        </div>
        <ChevronLeft size={18} className="text-tg-hint shrink-0" />
      </button>
      <button
        type="button"
        onClick={onMenu}
        aria-label="خيارات الحزمة"
        className="w-10 h-10 rounded-full flex items-center justify-center text-tg-hint shrink-0"
      >
        <MoreHorizontal size={20} />
      </button>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="glass-card flex items-center gap-3.5 p-3 animate-pulse">
      <div className="w-14 h-14 rounded-2xl bg-black/20" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-1/2 rounded bg-black/20" />
        <div className="h-3 w-1/4 rounded bg-black/15" />
      </div>
    </div>
  );
}

export function PacksBrowser({ onBack }: { onBack: () => void }) {
  const [packs, setPacks] = useState<PackInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<PackInfo | null>(null);
  const [menuPack, setMenuPack] = useState<PackInfo | null>(null);
  const [renamePack, setRenamePack] = useState<PackInfo | null>(null);
  const [deletePack, setDeletePack] = useState<PackInfo | null>(null);
  const [query, setQuery] = useState("");
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | null>(null);

  const say = (text: string) => {
    setNotice(text);
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 3200);
  };
  useEffect(() => () => void (noticeTimer.current && window.clearTimeout(noticeTimer.current)), []);

  const load = useCallback(async (fresh = false) => {
    setRefreshing(true);
    try {
      setPacks(await packApi.list(fresh));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تعذر التحميل");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patchPack = (name: string, patch: Partial<PackInfo>) =>
    setPacks((prev) => prev && prev.map((p) => (p.name === name ? { ...p, ...patch } : p)));
  const dropPack = (name: string) => setPacks((prev) => prev && prev.filter((p) => p.name !== name));

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return !packs ? null : q ? packs.filter((p) => p.title.toLowerCase().includes(q)) : packs;
  }, [packs, query]);

  if (selected) {
    return (
      <PackDetail
        pack={selected}
        onBack={() => setSelected(null)}
        onChanged={(patch) => {
          patchPack(selected.name, patch);
          setSelected((s) => (s ? { ...s, ...patch } : s));
        }}
        onDeleted={(gone) => {
          dropPack(selected.name);
          setSelected(null);
          if (gone) say(`«${selected.title}» محذوفة من تيليجرام، تمت إزالتها من القائمة`);
        }}
      />
    );
  }

  const copyFor = async (p: PackInfo) => {
    await copyText(packLink(p.name));
    setCopiedName(p.name);
    window.setTimeout(() => setCopiedName((n) => (n === p.name ? null : n)), 1800);
  };

  return (
    <div className="h-full overflow-y-auto">
      <ScreenHeader title="حزمي" onBack={onBack} />
      <div className="px-4 pt-3">
        <BannerCarousel />
      </div>

      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between px-1">
          <span className="text-tg-hint text-sm">{packs ? `${packs.length} حزمة` : " "}</span>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-sm text-tg-link disabled:opacity-60"
          >
            <RefreshCw size={14} strokeWidth={2.25} className={refreshing ? "animate-spin" : ""} />
            تحديث
          </button>
        </div>

        {packs && packs.length > 3 && (
          <div className="relative">
            <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-3.5 text-tg-hint pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ابحث في حزمي"
              dir="rtl"
              className="w-full rounded-xl glass-pill ps-10 pe-4 py-3 text-base outline-none placeholder:text-tg-hint"
            />
          </div>
        )}

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {packs === null && !error && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {packs && packs.length === 0 && (
          <div className="flex flex-col items-center text-center gap-3 py-10">
            <div className="w-16 h-16 rounded-full glass-pill flex items-center justify-center">
              <Package className="text-tg-hint" size={28} />
            </div>
            <p className="font-semibold">ما عندك حزم بعد</p>
            <p className="text-tg-hint text-sm max-w-[16rem]">أنشئ ملصقاً أولاً ثم أضفه إلى حزمة جديدة وستظهر هنا.</p>
          </div>
        )}

        {visible && packs && packs.length > 0 && visible.length === 0 && (
          <p className="text-tg-hint text-sm text-center py-6">لا توجد حزمة بهذا الاسم</p>
        )}

        {visible?.map((p) => (
          <PackRow key={p.name} pack={p} onOpen={() => setSelected(p)} onMenu={() => setMenuPack(p)} />
        ))}
      </div>

      <BottomSheet open={!!menuPack} onClose={() => setMenuPack(null)}>
        {menuPack && (
          <div>
            <p className="font-semibold mb-3 truncate">{menuPack.title}</p>
            <div className="glass-card overflow-hidden">
              <MenuRow
                icon={<ExternalLink size={19} strokeWidth={2} />}
                label="فتح في تيليجرام"
                onClick={() => {
                  openPackLink(packLink(menuPack.name));
                  setMenuPack(null);
                }}
              />
              <MenuRow
                icon={copiedName === menuPack.name ? <Check size={19} strokeWidth={2.5} /> : <Copy size={19} strokeWidth={2} />}
                label={copiedName === menuPack.name ? "تم نسخ الرابط" : "نسخ الرابط"}
                onClick={() => copyFor(menuPack)}
              />
              <MenuRow
                icon={<Pencil size={19} strokeWidth={2} />}
                label="تعديل الاسم"
                onClick={() => {
                  setRenamePack(menuPack);
                  setMenuPack(null);
                }}
              />
              <MenuRow
                icon={<Trash2 size={19} strokeWidth={2} />}
                label="حذف الحزمة"
                danger
                onClick={() => {
                  setDeletePack(menuPack);
                  setMenuPack(null);
                }}
              />
            </div>
          </div>
        )}
      </BottomSheet>

      <RenamePackSheet
        open={!!renamePack}
        name={renamePack?.name ?? ""}
        title={renamePack?.title ?? ""}
        onClose={() => setRenamePack(null)}
        onDone={(t) => renamePack && patchPack(renamePack.name, { title: t })}
        onGone={() => {
          if (!renamePack) return;
          dropPack(renamePack.name);
          say(`«${renamePack.title}» محذوفة من تيليجرام، تمت إزالتها من القائمة`);
        }}
      />
      <DeletePackSheet
        open={!!deletePack}
        name={deletePack?.name ?? ""}
        title={deletePack?.title ?? ""}
        onClose={() => setDeletePack(null)}
        onDone={() => deletePack && dropPack(deletePack.name)}
      />
      {notice &&
        createPortal(
          <div
            className="fixed left-1/2 z-[70] -translate-x-1/2 px-4 py-2.5 rounded-2xl text-sm shadow-lg text-center"
            style={{
              bottom: "max(24px, env(safe-area-inset-bottom))",
              maxWidth: "88vw",
              background: "rgba(30,34,43,.96)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.08)",
            }}
            role="status"
          >
            {notice}
          </div>,
          document.body,
        )}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-right ${danger ? "text-red-400" : ""}`}
    >
      <span className={`shrink-0 ${danger ? "" : "text-tg-hint"}`}>{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}
